import { useCallback, useEffect, useRef, useState } from 'react';
import { getInstagramContactIdentity, sendInstagramMessage, uploadInstagramAttachment, type InstagramContactIdentity } from '../services/instagram.ts';

export function useInstagramConversation(contactId: number | null) {
  const [identity, setIdentity] = useState<InstagramContactIdentity | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const selected = useRef(contactId);
  selected.current = contactId;
  const inFlight = useRef(false);
  const uncertain = useRef(new Map<string, string>());
  const reload = useCallback(async () => {
    const version = ++generation.current;
    if (!contactId) { setIdentity(null); setLoading(false); setError(''); return; }
    setLoading(true);
    try {
      const value = await getInstagramContactIdentity(contactId);
      if (value.contact_id !== contactId) throw new Error('Identidade divergente');
      if (generation.current === version) { setIdentity(value); setError(''); }
    } catch {
      if (generation.current === version) { setIdentity(null); setError('Não foi possível verificar esta conversa do Instagram.'); }
    } finally {
      if (generation.current === version) setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    setIdentity(null);
    void reload();
    return () => { ++generation.current; };
  }, [reload]);

  const send = useCallback(async (content: string, type: 'text' | 'image' | 'video' | 'audio' = 'text', attachmentId?: number) => {
    if (inFlight.current) throw new Error('Aguarde a conclusão do envio em andamento.');
    if (!contactId || identity?.contact_id !== contactId || selected.current !== contactId) throw new Error('Aguarde a verificação da conversa.');
    if (!(type === 'text' ? identity.capabilities.send_text : attachmentId ? identity.capabilities.upload_private_media : identity.capabilities.send_media_url)) throw new Error('O envio não está disponível nesta conversa.');
    const expiry = Date.parse(identity.window_expires_at || '');
    if (!Number.isFinite(expiry) || expiry <= Date.now()) throw new Error('Aguarde uma nova mensagem para responder pelo Instagram.');
    const fingerprint = JSON.stringify([contactId, identity.identity_id, type, content, attachmentId]);
    const existing = uncertain.current.get(fingerprint);
    const key = existing || `ig-human:${contactId}:${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
    uncertain.current.set(fingerprint, key);
    inFlight.current = true;
    setSending(true);
    try {
      const result = await sendInstagramMessage({ contact_id: contactId, identity_id: identity.identity_id,
        idempotency_key: key, content, message_type: type, ...(attachmentId ? { attachment_id: attachmentId } : {}) });
      if (result.status === 'sent' || result.status === 'failed') uncertain.current.delete(fingerprint);
      return result;
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  }, [contactId, identity]);

  const sendFile = useCallback(async (file: File) => {
    if (inFlight.current) throw new Error('Aguarde a conclusão do envio em andamento.');
    if (!contactId || identity?.contact_id !== contactId || selected.current !== contactId || !identity.capabilities.upload_private_media) throw new Error('O envio de arquivos não está disponível nesta conversa.');
    const fingerprint = JSON.stringify(['file', contactId, identity.identity_id, file.name, file.size, file.type, file.lastModified]);
    const key = uncertain.current.get(fingerprint) || `ig-upload:${contactId}:${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
    uncertain.current.set(fingerprint, key);
    inFlight.current = true;
    setSending(true);
    let attachment;
    try {
      attachment = await uploadInstagramAttachment({ contact_id: contactId, identity_id: identity.identity_id, idempotency_key: key, file });
      if (attachment.status !== 'ready') throw new Error('Não foi possível confirmar o arquivo. Nenhuma mensagem foi enviada.');
    } finally {
      inFlight.current = false;
      setSending(false);
    }
    const result = await send('', attachment.message_type, attachment.attachment_id);
    if (result.status === 'sent') uncertain.current.delete(fingerprint);
    return { ...result, content_url: attachment.content_url, message_type: attachment.message_type };
  }, [contactId, identity, send]);

  return { identity: identity?.contact_id === contactId ? identity : null, loading, sending, error, reload, send, sendFile };
}
